import React, { useEffect, useContext } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { GlobalStateContext } from "../../controller/GlobalState";
import { blogPosts } from "../../controller/global";
import ExploreOtherCaseStudy from "../../component/ExploreOtherCaseStudy";

const QUICK_FACTS = [
  { value: "React", label: "Frontend" },
  { value: ".NET Core API", label: "Backend" },
  { value: "React Native", label: "Mobile" },
  { value: "AWS", label: "Cloud" },
  { value: "Hardware + IoT", label: "Scope" },
];

const ARCHITECTURE_COMPONENTS = [
  {
    title: "Member State API (.NET Core on AWS)",
    desc: "The authoritative backend governing all member data, wallet operations, QR validation, access rule evaluation, offer logic, and transaction settlement. Every hardware device and application interface is a consumer of this single API.",
  },
  {
    title: "Secure QR Token Service",
    desc: "Embedded within the backend, generating time-limited, cryptographically signed tokens for gate and kiosk use. Short expiry windows prevent replay attacks and credential sharing without impacting the member experience.",
  },
  {
    title: "Hardware Communication Layer",
    desc: "A resilient integration service managing persistent, low-latency connections between the AWS backend and physical gate and kiosk devices at all branch locations, with local caching logic for graceful degradation during network disruptions.",
  },
  {
    title: "React Native Mobile App",
    desc: "Cross-platform member application for QR generation, wallet management, membership visibility, offer engagement, and transaction history on iOS and Android.",
  },
  {
    title: "React Management Portal",
    desc: "Web-based back-office platform for staff and management — CRM, membership administration, campaign management, and multi-branch reporting.",
  },
  {
    title: "AWS Cloud Infrastructure",
    desc: "Scalable, highly available hosting with independent deployment pipelines per environment, ensuring software updates can be released without service interruption to live hardware at branch locations.",
  },
];

const KEY_IMPACT = [
  "Members through gate in under 2 seconds with automated attendance logging",
  "Eliminated manual check-in queues at peak hours",
  "Shifted in-club purchases to fully self-serve kiosk model",
  "Real-time multi-branch dashboards for attendance, revenue, and offers",
  "Data-driven CRM for retention and targeted promotions",
  "Immediate physical enforcement of software decisions across all branches",
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const GymChainIntegration = () => {
  const caseStudy = blogPosts.find((post) => post.slug === "gym-chain-integration");
  const { setIsOpen } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Gym Chain Integration Case Study | InQmise Technologies";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "How we connected CRM, a React Native app, QR entry gates and payment kiosks into one system for a multi-location gym chain, on AWS.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies/gym-chain-integration");
  }, []);

  const openContactModal = () => setIsOpen(true);
  const goToCaseStudies = (e) => { e.preventDefault(); navigate("/case-studies"); };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header
        style={caseStudy?.image ? {
          backgroundImage: `linear-gradient(rgba(251,252,248,.5),rgba(251,252,248,.8)), url(${caseStudy.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="ea-hero ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href="/case-studies" onClick={goToCaseStudies}>← All Case Studies</a>
            <p className="ea-eyebrow">Case study · {caseStudy?.category}</p>
            <h1>Gym management software integration, from reception desk to gym floor</h1>
            <p className="ea-lede">
              How we designed and delivered a fully integrated gym ecosystem — bridging CRM, React
              Native mobile app, QR-enabled gate hardware, and kiosk payment systems into a single real-
              time platform on AWS.
            </p>
            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
              <a className="ea-text-link" href="/case-studies" onClick={goToCaseStudies}>Explore other case studies</a>
            </div>
          </Reveal>

          <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
            <div className="ea-engine-top"><span /> GYM CHAIN / QUICK FACTS <b>LIVE</b></div>
            {QUICK_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </header>

      {/* CLIENT BACKGROUND */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Client background</p>
          <h2 className="cd-h2">Client Background</h2>
          <div className="std-overview">
            <p>
              Our client is a growing multi-location gym chain serving thousands of active members across
              several cities. As the business scaled, its operational infrastructure had not kept pace:
              membership records lived in spreadsheets, check-ins were handled manually at reception,
              promotions were communicated informally, and in-club retail had no connection to member
              accounts whatsoever. Each new branch added complexity rather than efficiency, and the
              member experience was inconsistent across locations. Staff time was being consumed by
              administrative tasks that should have been automated, and management had no reliable view of
              attendance or revenue across the business in real time. The brief was unambiguous: build a
              unified platform that connects every part of the member journey — and make sure it works not
              just on screens, but physically at the club door and on the gym floor.
            </p>
          </div>
        </Reveal>
      </section>

      {/* THE CORE CHALLENGE */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The core challenge</p>
            <h2 className="cd-h2">The Core Challenge: Making Software Decisions Execute in the Physical World</h2>
            <div className="std-overview">
              <p>
                The hardest aspect of this engagement was not writing the software. It was making software
                decisions — membership status, wallet balance, access rights — execute reliably in the
                physical world, in real time, without friction. A gate that hesitates for three seconds at 7am
                during peak rush is not a minor inconvenience; it is a queue, and queues kill member
                experience. A kiosk that double-charges a member or fails to dispense after debiting their
                account erodes trust immediately and irreparably.
              </p>
              <p>
                This created a category of engineering problem that sits at the intersection of distributed
                systems, hardware integration, and user experience design. Every component — the React web
                portal, the .NET Core API, the React Native mobile app, the entry gate controllers, and the
                health drink kiosks — had to behave as a single coherent system sharing one live source of
                truth about every member's state. A membership suspended in the back-office portal had to be
                enforced at the gate within seconds. A wallet topped up in the app had to be available at the
                kiosk before the member had walked across the floor.
              </p>
              <p>
                Achieving that level of real-time consistency across software and physical hardware, deployed
                reliably on AWS across multiple branch locations, was the central technical challenge of the
                project — and the thing that fundamentally distinguishes this platform from a standard gym
                management tool.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CRM & MANAGEMENT PLATFORM */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Management platform</p>
          <h2 className="cd-h2">The CRM & Management Platform</h2>
          <div className="std-overview">
            <p>
              Built in React with a .NET Core API backend hosted on AWS, the management platform
              handled the full membership lifecycle: sign-ups, plan assignment, renewals, suspensions,
              cancellations, and tier management. A CRM layer tracked member history, visit patterns,
              communication preferences, and engagement data, giving staff the context to provide
              personalized service and giving management the intelligence to drive retention decisions.
            </p>
            <p>
              The offer and discount engine allowed staff to create targeted promotions by membership tier,
              visit frequency, or tenure — replacing ad-hoc discounts with a structured, auditable campaign
              system. Active offers were surfaced automatically to the relevant members in their mobile app.
              Each member account carried an integrated digital wallet that members could top up via the app. It served as
              the sole payment mechanism for in-club kiosk purchases. Multi-location reporting gave the
              ownership team a consolidated, real-time view of attendance, revenue, and wallet activity
              across all branches from a single screen.
            </p>
          </div>
        </Reveal>
      </section>

      {/* REACT NATIVE MOBILE APPLICATION */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Mobile application</p>
            <h2 className="cd-h2">The React Native Mobile Application</h2>
            <div className="std-overview">
              <p>
                The member-facing mobile application — built in React Native for cross-platform deployment on
                iOS and Android — was the physical key that unlocked the entire ecosystem. Its two most
                critical functions, gate access and kiosk payment, had to work instantly and without ambiguity
                every single time a member used them.
              </p>
              <p>
                Both were powered by a secure QR generation service embedded in the .NET Core backend.
                QR codes were dynamically generated, cryptographically signed, and time-bound — expiring
                after a short window to prevent screenshot sharing, credential forwarding, or replay attacks. The
                app was designed so that a member arriving at the gate could have their QR on screen within
                one or two taps from the lock screen, with no re-authentication required per visit. Beyond
                access, the app provided full membership visibility: plan status, wallet balance, transaction
                history, active offers, and renewal dates. Push notifications handled low-balance alerts, renewal
                reminders, and targeted campaign messages.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HARDWARE INTEGRATION: GATE ACCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Hardware integration</p>
          <h2 className="cd-h2">Hardware Integration: QR Gate Access</h2>
          <div className="std-overview">
            <p>
              The gate access integration was built around a hardware communication layer — a lightweight,
              always-on service running at each branch that maintained a persistent, low-latency connection
              between the physical gate controllers and the AWS-hosted .NET Core API.
            </p>
            <p>
              When a member presented their QR at the gate, the scan event was relayed to the backend in
              real time. The API validated the QR signature, evaluated the member's current state — active
              membership, no suspensions, no outstanding payment holds — and returned an access
              decision. On a valid response, the gate opened and an attendance record was written to the
              member's account instantly, with timestamp, branch ID, and entry type. On a failed scan, the
              gate remained closed and a status message was surfaced on the gate display — expired
              membership, suspended account, or unrecognized credential — clearly enough for the member
              to understand the issue without requiring staff intervention.
            </p>
            <p>
              The most operationally critical design decision was how the system would behave during a
              network interruption. A gate that fails closed during a brief AWS connectivity blip would create
              an unacceptable blockage at peak hours. We implemented a resilient local validation cache at
              each gate controller: members who had been successfully validated within a recent time window
              were eligible for short-window local admission during an outage, while members with new or
              flagged status required a live API response. The cache carried a deliberately short TTL — long
              enough to absorb brief disruptions, short enough to ensure that a genuine suspension could not
              be bypassed for more than a matter of minutes. All locally cached admissions were
              automatically reconciled back to the cloud record once connectivity was restored.
            </p>
          </div>
        </Reveal>
      </section>

      {/* HARDWARE INTEGRATION: KIOSK PAYMENTS */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Hardware integration</p>
            <h2 className="cd-h2">Hardware Integration: Kiosk Payments & Dispensing</h2>
            <div className="std-overview">
              <p>
                The health drink kiosk integration introduced a more complex challenge than the gate: a multi-
                step transaction that had to complete atomically across three systems — the member's wallet in
                the cloud, the kiosk dispense controller, and the audit log — with no possibility of partial
                success.
              </p>
              <p>
                When a member scanned their QR at a kiosk, the .NET Core backend executed a tightly
                sequenced atomic transaction: validate the QR signature and member identity, confirm the
                wallet balance was sufficient for the selected product, deduct the amount from the wallet, and
                dispatch a confirmed dispense signal to the kiosk hardware. If any step in that chain failed —
                network timeout, insufficient balance, hardware non-response — the entire transaction was
                rolled back. No charge without a confirmed dispense. No dispense without a confirmed charge.
              </p>
              <p>
                The system was fully idempotent: if a network retry caused the same request to arrive twice, the
                backend detected the duplicate and returned the original result without re-executing, eliminating
                any possibility of double-charging or double-dispensing.
                Every kiosk transaction was written to the member's account in real time and was immediately
                visible in both the app's transaction history and the back-office portal. Branch managers could
                report on kiosk revenue, product popularity, and per-member spend alongside attendance data
                in the same unified dashboard — a first for this client.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Architecture</p>
          <h2 className="cd-h2">Architecture: One Source of Truth Across Hardware and Software</h2>
          <div className="std-overview">
            <p>
              The platform was architected around a single authoritative member state service hosted on
              AWS. Every peripheral system — the React management portal, the React Native app, the gate
              controllers, and the kiosks — communicated exclusively through the .NET Core API and read
              from and wrote to the same underlying data store. This single-source-of-truth design meant that
              state changes propagated everywhere without synchronization complexity: a membership
              suspended in the portal was enforced at every gate across every branch within seconds; a
              wallet top-up on the app was available at the nearest kiosk before the member had crossed the
              room.
            </p>
          </div>
        </Reveal>
        <p style={{ margin: "24px 0 0", fontFamily: "Syne, sans-serif", fontSize: "var(--t-lg)", fontWeight: 700, letterSpacing: "-.04em" }}>Core Architecture Components</p>
        <div className="re-numbered-list">
          {ARCHITECTURE_COMPONENTS.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-numbered-row" key={item.title}>
              <div className="re-numbered-row-num">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUTCOMES & IMPACT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Outcomes & impact</p>
            <h2 className="cd-h2">Outcomes & Impact</h2>
            <div className="std-overview">
              <p>
                The platform eliminated the manual check-in queue that had defined peak-hour operations at
                every branch. Members walk in, scan, and are through the gate in under two seconds, with
                attendance automatically logged against their account. In-club purchases shifted to a fully self-
                serve model at the kiosk, removing cash handling, card terminals, and staff involvement from
                routine transactions. Administrative time that had been consumed by check-ins, manual
                records, and reconciliation was redirected to member-facing service.
              </p>
              <p>
                For management, the move from disconnected manual records to a unified real-time platform
                delivered a quality of visibility the business had never had: live attendance dashboards, wallet
                revenue trends, offer redemption rates, and side-by-side branch comparisons — all in a single
                view. The CRM gave the business its first reliable foundation for data-driven retention and
                targeted promotional activity.
              </p>
              <p>
                Most significantly, the hardware-software integration delivered a capability that generic gym
                management platforms cannot replicate: a system where a decision made in the software has
                an immediate, physical consequence in the real world. Suspend a membership — the gate
                knows in seconds. Issue a discount — the app surfaces it before the member's next visit. Top
                up a wallet — the kiosk reflects it immediately. That tight, real-time loop between digital
                management and physical operations is what makes this platform genuinely transformative for a
                business that lives in both worlds simultaneously.
              </p>
            </div>
            <p style={{ margin: "24px 0 12px", fontFamily: "Syne, sans-serif", fontSize: "var(--t-sm)", fontWeight: 700, letterSpacing: "-.03em" }}>Key Impact Metrics</p>
            <ul className="re-bare-list">
              {KEY_IMPACT.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE OTHER CASE STUDIES */}
      <ExploreOtherCaseStudy currentSlug="gym-chain-integration" />

      {/* CTA */}
      <section className="ea-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Ready when you are</div>
            <h2 style={{ margin: "16px 0" }}>Ready to Integrate Your Physical and Digital Operations?</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              Let's discuss how we can build comprehensive hardware-software solutions that transform your business.
            </p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Get in Touch</button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default GymChainIntegration;
